import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';

export default function BannerNotificationsStack({ banners, onNavigate, onView, onDismiss }) {
  const viewedIdsRef = React.useRef(new Set());

  React.useEffect(() => {
    if (!Array.isArray(banners) || typeof onView !== 'function') {
      return;
    }
    banners.forEach((banner) => {
      const bannerId = banner?.id;
      if (!bannerId || viewedIdsRef.current.has(bannerId)) {
        return;
      }
      viewedIdsRef.current.add(bannerId);
      onView(bannerId);
    });
  }, [banners, onView]);

  if (!Array.isArray(banners) || banners.length === 0) {
    return null;
  }

  return (
    <Stack spacing={1.5}>
      {banners.map((banner) => (
        <Card
          key={banner.id || `${banner.title}-${banner.created_at || ''}`}
          sx={{
            borderRadius: '10px',
            backgroundColor: '#4871E6',
            color: 'primary.contrastText',
            boxShadow: 0,
            cursor: banner?.cta_url ? 'pointer' : 'default',
          }}
          onClick={banner?.cta_url ? () => onNavigate?.(banner.cta_url) : undefined}
        >
          <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, opacity: 0.8, mb: 1 }}>
                  <ConfirmationNumberOutlinedIcon sx={{ fontSize: 20 }} />
                  <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase', color: '#121843', opacity: 0.8 }}>
                    Anuncio
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: 20, fontWeight: 800, lineHeight: 1.2, mb: 1, color: '#121843' }}>
                  {banner?.title || 'Anuncio'}
                </Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 400, opacity: 0.85, color: '#121843' }}>
                  {banner?.main_message || banner?.sub_message || ''}
                </Typography>
                {banner?.sub_message && banner?.main_message && banner.sub_message !== banner.main_message && (
                  <Typography sx={{ fontSize: 13, fontWeight: 400, opacity: 0.78, mt: 1, color: '#121843' }}>
                    {banner.sub_message}
                  </Typography>
                )}
              </Box>
              <IconButton
                onClick={(event) => {
                  event.stopPropagation();
                  onDismiss?.(banner?.id);
                }}
                sx={{
                  color: '#121843',
                  border: '1px solid rgba(18,24,67,0.22)',
                  width: 34,
                  height: 34,
                  borderRadius: '10px',
                  flexShrink: 0,
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            {banner?.cta_url && (
              <Box sx={{ mt: 2 }}>
                <Button
                  onClick={(event) => {
                    event.stopPropagation();
                    onNavigate?.(banner.cta_url);
                  }}
                  sx={{
                    height: 40,
                    px: 3,
                    borderRadius: '10px',
                    backgroundColor: 'background.paper',
                    color: '#121843',
                    fontWeight: 800,
                    '&:hover': { backgroundColor: 'background.paper' },
                  }}
                >
                  {banner?.cta_name || 'Ver más'}
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
}
